// Decide which (if any) newly-appeared entry the timeline logger should focus
// when the draft's exercise list changes. Pure so the hydration edge cases can
// be unit-tested — auto-focusing during program/plan/repeat hydration would
// violate Option B (a hydrated workout must start EMPTY, no bound exercise).
//
// The caller passes the previous and next exercise-id lists (in order). It runs
// on every list change: user add (1 or many), in-place swap, remove, and the
// initial hydration.
export function firstAddedToFocus(
  prevIds: string[],
  nextIds: string[],
): string | null {
  const added = nextIds.filter((id) => !prevIds.includes(id));
  if (added.length === 0) return null;
  // Exactly one new id: either a user add of a single exercise, OR an in-place
  // SWAP / pick-alternative (the old id is removed and the new one takes its
  // slot at the same index). Both should focus the new exercise — the
  // long-standing behaviour. (A 1-exercise program/repeat hydration also lands
  // here and focuses that lone exercise — the documented, benign edge.)
  if (added.length === 1) return added[0]!;
  // Several new ids at once: focus the first ONLY when it is a pure append onto
  // a NON-EMPTY roster — the picker's multi-select "Add {n}" mid-workout. A
  // []→[many] change or a full/partial roster REPLACE is hydration
  // (program/plan/repeat) and must stay unfocused (Option B). "Pure append onto
  // non-empty" = the roster already had entries and every one of them survived.
  const pureAppendToNonEmpty =
    prevIds.length > 0 && prevIds.every((id) => nextIds.includes(id));
  return pureAppendToNonEmpty ? added[0]! : null;
}
